// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentCartState,
    hasProductsWithInvalidPrice,
    isOutOfStock,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@werner/App/Components/Notify";
import { parseBoolean } from "@werner/XDL/Utilities";
import React, { FunctionComponent } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        cart,
        cartNotPriced: cart?.cartNotPriced,
        hasInsufficientInventory: cart?.hasInsufficientInventory && cart?.fulfillmentMethod === FulfillmentMethod.Ship,
        hasInvalidPrice: hasProductsWithInvalidPrice(cart),
        failedToGetRealTimeInventory: cart?.failedToGetRealTimeInventory,
        hasInsufficientInventoryForPickup:
            cart?.fulfillmentMethod === FulfillmentMethod.PickUp &&
            cart?.cartLines?.some(c => c.availability?.messageType === 2),
        selectedShipTo: state.context.session.shipToId,
    };
};

export interface CartApproverMessageStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
}

export const cartApproverMessageStyles: CartApproverMessageStyles = {
    approvingWrapper: {
        css: css`
            text-align: center;
            padding: 10px;
            background-color: ${getColor("info.main")};
        `,
    },
    approvingText: {
        css: css`
            width: 100%;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = cartApproverMessageStyles;
const CartApproverMessage = ({
    cart,
    cartNotPriced,
    hasInsufficientInventory,
    hasInvalidPrice,
    failedToGetRealTimeInventory,
    hasInsufficientInventoryForPickup,
}: Props) => {
    if (!cart || cart.cartLines?.length === 0) {
        return null;
    }
    const productsCannotBePurchased = cart.cartLines?.some(o => o.isRestricted || !o.isActive);

    const hasNonStockItems = cart.cartLines?.some(line => parseBoolean(line.properties.isNonStockItem));
    // XNG-Changes
    const hasEventItems = cart.cartLines?.find(cartLine => parseBoolean(cartLine.properties.isEventOrderLine));

    return (
        <>
            {/* <Typography {...styles.approvingText} data-test-selector="cart_approvingOrderMessage">
                {translate(
                    "You are approving order #{0} for {1}",
                    cart.orderNumber,
                    cart.initiatedByUserName?.toUpperCase(),
                )}
            </Typography> */}
            {cart.quoteRequiredCount > 0 && (
                <Notify type="warning" data-test-selector="cart_approvingOrderMessage">
                    {siteMessage("Rfq_QuoteRequiredMsg")}
                </Notify>
            )}
            {cart.isAwaitingApproval && (
                <Notify type="info" data-test-selector="cart_approvingOrderMessage">
                    {translate("Approving Order for {0}", cart.initiatedByUserName)}
                </Notify>
            )}
            {productsCannotBePurchased && (
                <Notify type="warning" data-test-selector="cartlineHeader_warningProductsCannotBePurchased">
                    {siteMessage("Cart_ProductsCannotBePurchased")}
                </Notify>
            )}
            {cartNotPriced && (
                <Notify type="warning" data-test-selector="cartlineHeader_NoPriceAvailableAtCheckout">
                    {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                </Notify>
            )}
            {(hasInsufficientInventory || hasInsufficientInventoryForPickup) && (
                <Notify type="warning" data-test-selector="cartlineHeader_warningInsufficientInventoryAtCheckout">
                    {siteMessage("Cart_InsufficientInventoryAtCheckout")}
                </Notify>
            )}
            {!cartNotPriced && hasInvalidPrice && (
                <Notify type="warning">{siteMessage("Cart_InvalidPriceAtCheckout")}</Notify>
            )}
            {failedToGetRealTimeInventory && (
                <Notify type="warning">{siteMessage("Cart_NoInventoryAvailableAtCheckout")}</Notify>
            )}
            {hasNonStockItems && <Notify type="warning">{siteMessage("Werner_CartHasNonStockItem")}</Notify>}
            {/* XNG-Changes  */}
            {hasEventItems && <Notify type="warning">{siteMessage("Werner_CartHasEventItems")}</Notify>}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartApproverMessage),
    definition: {
        displayName: "Cart Approver Message",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
